import { DateTime } from '@brightdrop/localization-core';
import type { MRT_ColumnDef } from '@brightdrop/table-ui';
import { Typography } from '@mui/material';
import type { VehiclesEnrollmentViewItemDto } from '~/api/generated-api';
import { TABLE_COLUMN_MIN_WIDTH } from '~/common/components/bps/table/table.constants';
import { translateText } from '~/common/components/TranslatedLabel/TranslatedLabel';
import VehicleIdentityCellRenderer from '~/features/assets/components/VehicleIdentityCellRenderer/VehicleIdentityCellRenderer';
import {
  ENROLLMENT_COLUMNS,
  type EnrollmentColumn,
} from '~/features/integrations/partners/types';

export type EnrollmentRow = VehiclesEnrollmentViewItemDto;
type EnrollmentColumnDef = MRT_ColumnDef<EnrollmentRow>;

/** The enrollment view API ignores `search.sort`, so no column sorts until it applies one. */
export const IS_SORT_SUPPORTED = false;

const TRANSLATION_KEYS = {
  vehicle: 'common:vehicle',
  added: 'asset:added',
} as const;

const VehicleCell = ({ row }: { row: EnrollmentRow }) => {
  const { vehicle } = row;
  return (
    // The shared renderer redacts the name but not the VIN.
    <div data-heap-redact-text data-testid="enrollment-table-vehicle-cell">
      <VehicleIdentityCellRenderer
        vin={vehicle.vin}
        name={vehicle.name ?? vehicle.vin}
        imageUrl={vehicle.vehicleImageSource ?? ''}
        vehicleTypeDetail={[vehicle.year, vehicle.make, vehicle.model]
          .filter(Boolean)
          .join(' ')}
      />
    </div>
  );
};

const AddedCell = ({ enrolledAt }: { enrolledAt?: string }) => (
  <Typography variant="small_body_regular">
    <DateTime value={enrolledAt} style="dateTime" withZone />
  </Typography>
);

const VEHICLE_COLUMN_DEF = (): EnrollmentColumnDef => ({
  id: 'vehicle',
  // The table's loading skeleton passes placeholder rows that carry no vehicle.
  accessorFn: (row) => row.vehicle?.name ?? row.vehicle?.vin,
  header: translateText(TRANSLATION_KEYS.vehicle),
  minSize: TABLE_COLUMN_MIN_WIDTH,
  enableSorting: IS_SORT_SUPPORTED,
  Cell: ({ row }) => <VehicleCell row={row.original} />,
});

const ENROLLMENT_COLUMN_DEFS: Record<
  EnrollmentColumn,
  () => EnrollmentColumnDef
> = {
  [ENROLLMENT_COLUMNS.ADDED]: () => ({
    id: ENROLLMENT_COLUMNS.ADDED,
    accessorKey: 'enrolledAt',
    header: translateText(TRANSLATION_KEYS.added),
    minSize: TABLE_COLUMN_MIN_WIDTH,
    enableSorting: IS_SORT_SUPPORTED,
    Cell: ({ row }) => <AddedCell enrolledAt={row.original.enrolledAt} />,
  }),
};

/**
 * The vehicle column followed by the tab's columns, in the order the tab lists them.
 * Resolves headers with translateText, so the caller must subscribe to `integrations`, `common`, and `asset`.
 * @param columns - The tab's `columns`, from its `ManageTab` entry
 */
export const getEnrollmentTableColumns = (
  columns: EnrollmentColumn[] = []
): EnrollmentColumnDef[] => [
  VEHICLE_COLUMN_DEF(),
  ...columns.map((column) => ENROLLMENT_COLUMN_DEFS[column]()),
];
